import { HttpParams } from '@angular/common/http';
import {
	CustomHttpUrlEncodingCodec,
	FilterOperatorAbbreviations,
	FilterTypes,
	Meta,
	PropertyFilter,
} from '@agilox/common';
import { Sort } from '@angular/material/sort';

/**
 * Builds HTTP parameters for API requests with custom encoding.
 */
export class HttpParamsBuilder {
	static buildParameters(
		page?: Meta,
		sort?: Sort,
		search?: string,
		filter?: object,
		operatorFilter?: PropertyFilter[]
	): HttpParams {
		let stringParam = '';

		if (search) {
			stringParam += `search=${encodeURIComponent(search)}`;
		}
		if (page) {
			stringParam += this.objectToParams(page, 'page');
		}
		if (sort) {
			stringParam += this.toSortParams(sort);
		}
		if (filter) {
			stringParam += this.objectToParams(filter, 'filter');
		}
		if (operatorFilter) {
			stringParam += this.buildOperatorFilterParams(operatorFilter, 'filter');
		}

		/**
		 * Strip first '&' if it is the first character.
		 * This is necessary because the HttpParams constructor
		 * will otherwise interpret the first '&' as a key-value pair.
		 * This will output e.g.
		 * stringParam = '&page[size]=50'
		 * => ?=&page[size]=50
		 * instead of
		 * ?page[size]=50
		 */
		// strip first & if it is the first character
		if (stringParam.length > 0 && stringParam.startsWith('&')) {
			stringParam = stringParam.substring(1);
		}

		return new HttpParams({
			fromString: stringParam,
			encoder: new CustomHttpUrlEncodingCodec(),
		});
	}

	private static toSortParams(sort: Sort): string {
		if (sort.direction == '') {
			return '';
		}
		const direction = sort.direction === 'desc' ? '-' : '';
		return `&sort=${direction}${encodeURIComponent(sort.active)}`;
	}

	private static objectToParams(data: object, key: string): string {
		let tempString = '';
		Object.entries(data).forEach(([propertyKey, value]) => {
			if (value && !Array.isArray(value)) {
				tempString += `&${key}[${propertyKey}]=${encodeURIComponent(value)}`;
			}
			if (Array.isArray(value) && value.length > 0) {
				tempString += this.arrayToParams(key, propertyKey, value);
			}
		});
		return tempString;
	}

	private static buildOperatorFilterParams(data: PropertyFilter[], key: string): string {
		let tempString = '';
		for (const propertyFilter of data) {
			if (propertyFilter.value === undefined) {
				continue;
			}
			let value = undefined;

			switch (propertyFilter.type) {
				case FilterTypes.BOOLEAN:
					value = propertyFilter.value ? 1 : 0;
					break;
				case FilterTypes.DATE:
					value = new Date(propertyFilter.value).getTime() / 1000;
					break;
				default:
					value = propertyFilter.value;
					break;
			}

			const operator = FilterOperatorAbbreviations[propertyFilter.operator];
			tempString += `&${key}[${propertyFilter.property}${operator ? '.' + operator : ''}]=${encodeURIComponent(value)}`;
		}
		return tempString;
	}

	private static arrayToParams(key: string, propertyKey: string, data: any[]): string {
		return `&${key}[${propertyKey}]=${data.map((value) => encodeURIComponent(value)).join(',')}`;
	}
}
